import { useNavigate } from 'react-router';
import { AlertTriangle, Home, ArrowLeft } from 'lucide-react';
import { useContext } from 'react';
import { LanguageContext } from '../contexts/LanguageContext';

// Fallback translations
const fallback = {
  pageNotFound: 'Page Not Found',
  pageNotFoundDetails: 'The page you are looking for does not exist.',
  goHome: 'Go to Home',
  back: 'Back',
  persistentIssue: 'If the issue persists, please contact support.'
};

export function NotFoundPage() {
  const navigate = useNavigate();
  const context = useContext(LanguageContext);
  const t = (key: string) => {
    if (context?.t) {
      return context.t(key);
    }
    const k = key.replace('errorBoundary.', '');
    return fallback[k as keyof typeof fallback] || key;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <div className="flex flex-col items-center text-center">
          {/* Error Icon */}
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-red-600" />
          </div>

          {/* Error Status */}
          <div className="text-6xl font-bold text-slate-900 mb-4">
            404
          </div>

          {/* Error Message */}
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            {t('errorBoundary.pageNotFound')}
          </h1>

          {/* Error Details */}
          <p className="text-slate-600 mb-8 max-w-md">
            {t('errorBoundary.pageNotFoundDetails')}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-300 transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('back') || 'Geri'}
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Home className="w-5 h-5" />
              {t('errorBoundary.goHome')}
            </button>
          </div>

          {/* Help Text */}
          <p className="text-sm text-slate-500 mt-8">
            {t('errorBoundary.persistentIssue')}
          </p>
        </div>
      </div>
    </div>
  );
}

NotFoundPage.displayName = 'NotFoundPage';